import type {
  ConnectionId, ConversationId, MessageId, ProfileId, SubjectId, UserId,
} from './ids.js'
import type { Channel, Conversation, Message, MessageContent, Subject } from './entities.js'
import type { ManifestAction } from './actions.js'

/** Dashboard-configured pre-chat qualification form, delivered in the manifest. */
export interface PreChatConfig {
  enabled:    boolean
  showWhen?:  'always' | 'offline'
  fields?:    ('name' | 'email' | 'phone')[]
  topics?:    string[]
  callbackOption?: boolean
  title?:     string
}

// ── Wire protocol ─────────────────────────────────────────────────────────────
// One shared contract, imported by server + widget + dashboard. A change here is
// a compile error in every consumer — which is the whole reason this lives in a
// shared package instead of being hand-copied three times.

export type ErrorCode =
  | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST'
  | 'RATE_LIMITED' | 'PAYLOAD_TOO_LARGE' | 'CONFLICT' | 'INTERNAL'

export type ClientFrame =
  | { type: 'auth';    token: string }
  // Open an existing conversation, or find-or-create one. Find-or-create keys on
  // (guest, subject) when subjectId is given; otherwise a fresh conversation.
  | { type: 'open';    conversationId?: ConversationId; subjectId?: SubjectId; profileId?: ProfileId; pageUrl?: string; pageTitle?: string; subjectTitle?: string; subjectMeta?: string; linkFrom?: UserId;
      /** Host-supplied display info for the guest — persisted onto the
       *  conversation server-side so agents see who they're talking to.
       *  Display metadata only, never used for authorization. */
      userInfo?: { name?: string; email?: string; avatar?: string; meta?: Record<string, string> } }
  | { type: 'send';    conversationId: ConversationId; clientMsgId: string; content: MessageContent; replyToId?: MessageId; via?: Channel[] }
  | { type: 'sync';    conversationId: ConversationId; sinceSeq: number }          // catch-up after cursor
  | { type: 'history'; conversationId: ConversationId; beforeSeq: number; limit?: number }  // load older
  | { type: 'read';    conversationId: ConversationId; seq: number }               // read up to seq
  | { type: 'typing';  conversationId: ConversationId; isTyping: boolean; preview?: string }
  | { type: 'react';   conversationId: ConversationId; messageId: MessageId; emoji: string; remove?: boolean }
  | { type: 'edit';    conversationId: ConversationId; messageId: MessageId; content: MessageContent }
  | { type: 'delete';  conversationId: ConversationId; messageId: MessageId }
  | { type: 'invoke';  conversationId: ConversationId; actionId: string; clientInvokeId: string; inputs?: Record<string, unknown> }
  | { type: 'assign';  conversationId: ConversationId; agentId: UserId | null }   // null = unassign
  | { type: 'tag';     conversationId: ConversationId; tag: string; remove?: boolean }
  | { type: 'note';    conversationId: ConversationId; clientMsgId: string; text: string }   // internal note
  | { type: 'agent_status'; status: 'online' | 'away' | 'offline' }   // agent sets their availability
  | { type: 'pubkey'; conversationId: ConversationId; key: string }
  // X3DH async E2E: a client uploads a batch of one-time prekeys so peers can
  // encrypt to them while they are offline. The server stores them opaquely and
  // vends one on demand — it never derives or uses the keys.
  | { type: 'uploadPrekeys'; identityKey: string; signedPrekey: string; signedPrekeyId: string; signature: string; oneTimePrekeys: string[] }
  | { type: 'fetchPrekey'; targetUserId: UserId }
  // Inbox stream subscription — used by the agent dashboard, which reuses this
  // ConnectionManager. Typed here so the dashboard doesn't need `as never`.
  | { type: 'subscribe_inbox' }
  | { type: 'unsubscribe_inbox' }
  | { type: 'ping' }

export type ServerFrame =
  | { type: 'authed';       userId: UserId; connectionId: ConnectionId }
  | { type: 'opened';       conversation: Conversation; subject?: Subject }
  | { type: 'manifest';     conversationId: ConversationId; actions: ManifestAction[]; version: number; name?: string; theme?: { accent: string }; e2e?: boolean; offline?: boolean; offlineMessage?: string; whiteLabel?: boolean; launcherMessage?: { title: string; subtitle?: string }; suggestedQuestions?: string[]; preChat?: PreChatConfig }
  | { type: 'message';      message: Message }
  | { type: 'ack';          clientMsgId: string; messageId: MessageId; seq: number; ts: number }
  | { type: 'delivered';    conversationId: ConversationId; seq: number; to: UserId }
  | { type: 'read';         conversationId: ConversationId; seq: number; by: UserId }
  | { type: 'sync';         conversationId: ConversationId; messages: Message[] }
  | { type: 'history';      conversationId: ConversationId; messages: Message[]; hasMore: boolean }
  | { type: 'typing';       conversationId: ConversationId; userId: UserId; isTyping: boolean; preview?: string }
  | { type: 'reaction';     conversationId: ConversationId; messageId: MessageId; emoji: string; by: UserId; removed: boolean }
  | { type: 'edited';       conversationId: ConversationId; messageId: MessageId; content: MessageContent; editedAt: number }
  | { type: 'deleted';      conversationId: ConversationId; messageId: MessageId; ts: number }
  | { type: 'state';        conversationId: ConversationId; state: string }
  | { type: 'assigned';     conversationId: ConversationId; agentId: UserId | null }
  | { type: 'tagged';       conversationId: ConversationId; tag: string; removed: boolean }
  | { type: 'visitor_count'; count: number }   // broadcast to agents: guests currently connected
  | { type: 'agent_status_changed'; agentId: UserId; status: 'online' | 'away' | 'offline' }
  // Live sentiment of a guest's most recent message — relayed to agents only so
  // the inbox can flag frustrated conversations as they happen.
  | { type: 'sentiment';    conversationId: ConversationId; label: 'positive' | 'neutral' | 'frustrated'; score: number }
  | { type: 'subjectState'; subjectId: SubjectId; state: string }
  | { type: 'presence';     conversationId: ConversationId; userId: UserId; status: 'online' | 'offline'; lastSeen?: number }
  | { type: 'invoked';      clientInvokeId: string; ok: boolean; error?: string }
  | { type: 'error';        code: ErrorCode; message: string }
  | { type: 'peerkey'; conversationId: ConversationId; userId: UserId; key: string }
  // X3DH bundle vended to a requesting client so they can encrypt to an offline peer.
  // Contains null when the target user has no registered prekeys.
  | { type: 'prekeyBundle'; targetUserId: UserId; bundle: { identityKey: string; signedPrekey: string; signedPrekeyId: string; signature: string; oneTimePrekey?: string } | null }
  | { type: 'pong' }
  // Live inbox update for the guest's OWN conversation list (widget list socket
  // subscribes via `subscribe_inbox`). `patch` mirrors the agent inbox patch; the
  // list re-fetches on receipt, so only `kind`/`conversationId` are load-bearing.
  | { type: 'inbox_event'; kind: 'new' | 'update'; conversationId: ConversationId; patch?: Record<string, unknown> }

/** Limits referenced by both ends so validation stays consistent. */
export const LIMITS = {
  MAX_TEXT_LEN:       8_000,
  MAX_HISTORY_LIMIT:  100,
  DEFAULT_HISTORY:    50,
} as const
